import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const BORE_VOICE_COUNT = 8;
export const BORE_OUTPUT_CEILING = 0.95;
export type BoreModel = 'reed' | 'lip';

export interface BoreVoiceParameters {
  readonly model: BoreModel;
  readonly detuneCents: number;
  readonly boreLoss: number;
  readonly brightness: number;
  readonly nonlinearity: number;
  readonly pressureSlewSeconds: number;
  readonly gain: number;
}

export interface TubeReedLipOptions {
  readonly sampleRate: number;
  readonly maxBlockSize: number;
}

type Mutable<T> = { -readonly [Key in keyof T]: T[Key] };

interface BoreVoice {
  readonly delay: Float64Array;
  readonly parameters: Mutable<BoreVoiceParameters>;
  active: boolean;
  frequencyHz: number;
  delaySamples: number;
  writeIndex: number;
  terminationState: number;
  lipState: number;
  previousFlow: number;
  pressure: number;
  targetPressure: number;
  releaseStep: number;
  releaseSamples: number;
  rng: number;
}

const TERMINATION_MAX_COEFFICIENT = 0.85;
const DEFAULT_SEED = 0x6d2b79f5;
const DEFAULTS: Readonly<BoreVoiceParameters> = Object.freeze({
  model: 'reed',
  detuneCents: 0,
  boreLoss: 0.15,
  brightness: 1,
  nonlinearity: 0.5,
  pressureSlewSeconds: 0.01,
  gain: 0.7,
});

const bounded = (name: string, value: number, minimum: number, maximum: number): number => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${name} must be finite and in [${minimum}, ${maximum}]`);
  }
  return value;
};

const integer = (name: string, value: number, minimum: number, maximum: number): number => {
  bounded(name, value, minimum, maximum);
  if (!Number.isInteger(value)) throw new RangeError(`${name} must be an integer`);
  return value;
};

const canonicalSeed = (seed: number): number => {
  integer('seed', seed, 0, 0xffff_ffff);
  return seed === 0 ? DEFAULT_SEED : seed >>> 0;
};

const nextNoise = (voice: BoreVoice): number => {
  let value = voice.rng >>> 0;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  voice.rng = value >>> 0;
  return voice.rng / 0x8000_0000 - 1;
};

const clamp = (value: number, minimum: number, maximum: number): number => Math.max(minimum, Math.min(maximum, value));
const flush = (value: number): number => Math.abs(value) < 1e-30 ? 0 : value;
const validateCallerBuffers = (left: Float32Array, right: Float32Array, frames: number, maxBlockSize: number): void => {
  if (!(left instanceof Float32Array) || !(right instanceof Float32Array)) throw new TypeError('left and right must be Float32Array caller buffers');
  if (!Number.isInteger(frames) || frames < 0 || frames > maxBlockSize || left.length < frames || right.length < frames) {
    throw new RangeError('caller buffers must cover frames within maxBlockSize');
  }
};

export class TubeReedLipResonator {
  readonly sampleRate: number;
  readonly maxBlockSize: number;
  readonly maximumFrequencyHz: number;
  private readonly voices: readonly BoreVoice[];

  constructor(options: TubeReedLipOptions) {
    this.sampleRate = bounded('sampleRate', options.sampleRate, 8_000, 192_000);
    this.maxBlockSize = integer('maxBlockSize', options.maxBlockSize, 1, 16_384);
    this.maximumFrequencyHz = Math.min(4_000, this.sampleRate * 0.1);
    const capacity = Math.ceil(this.sampleRate / 35) + 4;
    this.voices = Array.from({ length: BORE_VOICE_COUNT }, () => ({
      delay: new Float64Array(capacity),
      parameters: { ...DEFAULTS },
      active: false,
      frequencyHz: 440,
      delaySamples: this.sampleRate / (2 * 440),
      writeIndex: 0,
      terminationState: 0,
      lipState: 0,
      previousFlow: 0,
      pressure: 0,
      targetPressure: 0,
      releaseStep: 0,
      releaseSamples: 0,
      rng: DEFAULT_SEED,
    }));
  }

  getVoiceParameters(index: number): Readonly<BoreVoiceParameters> {
    return Object.freeze({ ...this.voice(index).parameters });
  }

  setVoiceParameters(index: number, update: Partial<BoreVoiceParameters>): void {
    const voice = this.voice(index);
    const next = { ...voice.parameters, ...update };
    this.validateParameters(next);
    const delaySamples = this.delayFor(voice.frequencyHz, next);
    Object.assign(voice.parameters, next);
    voice.delaySamples = delaySamples;
  }

  noteOn(index: number, frequencyHz: number, pressure: number, seed: number): void {
    const voice = this.voice(index);
    bounded('frequencyHz', frequencyHz, 35, this.maximumFrequencyHz);
    bounded('pressure', pressure, 0, 1);
    const validSeed = canonicalSeed(seed);
    const delaySamples = this.delayFor(frequencyHz, voice.parameters);

    this.clearVoiceState(voice);
    voice.frequencyHz = frequencyHz;
    voice.delaySamples = delaySamples;
    voice.targetPressure = pressure;
    voice.rng = validSeed;
    voice.active = pressure > 0;

    if (pressure > 0) {
      const excitationLength = Math.max(2, Math.ceil(delaySamples));
      let mean = 0;
      for (let index = 0; index < excitationLength; index += 1) {
        const sample = nextNoise(voice) * pressure * 0.025;
        voice.delay[index] = sample;
        mean += sample;
      }
      mean /= excitationLength;
      for (let index = 0; index < excitationLength; index += 1) voice.delay[index] = voice.delay[index]! - mean;
      voice.writeIndex = excitationLength % voice.delay.length;
    }
  }

  noteOff(index: number): void {
    const voice = this.voice(index);
    voice.targetPressure = 0;
    voice.releaseSamples = Math.max(1, Math.round(this.sampleRate * 0.02));
    voice.releaseStep = voice.pressure / voice.releaseSamples;
  }

  reset(index?: number): void {
    if (index === undefined) {
      for (const voice of this.voices) this.clearVoiceState(voice);
      return;
    }
    this.clearVoiceState(this.voice(index));
  }

  processInto(left: Float32Array, right: Float32Array, frames = left.length): void {
    validateCallerBuffers(left, right, frames, this.maxBlockSize);
    for (let frame = 0; frame < frames; frame += 1) {
      let mixedLeft = 0;
      let mixedRight = 0;
      for (let index = 0; index < BORE_VOICE_COUNT; index += 1) {
        const voice = this.voices[index]!;
        if (!voice.active) continue;
        const sample = this.processVoice(voice);
        const angle = index / (BORE_VOICE_COUNT - 1) * Math.PI * 0.5;
        const output = sample * voice.parameters.gain * 0.18;
        mixedLeft += output * Math.cos(angle);
        mixedRight += output * Math.sin(angle);
      }
      left[frame] = clamp(Number.isFinite(mixedLeft) ? mixedLeft : 0, -BORE_OUTPUT_CEILING, BORE_OUTPUT_CEILING);
      right[frame] = clamp(Number.isFinite(mixedRight) ? mixedRight : 0, -BORE_OUTPUT_CEILING, BORE_OUTPUT_CEILING);
    }
  }

  private processVoice(voice: BoreVoice): number {
    this.advancePressure(voice);
    const size = voice.delay.length;
    let readPosition = voice.writeIndex - voice.delaySamples;
    while (readPosition < 0) readPosition += size;
    const base = Math.floor(readPosition);
    const fraction = readPosition - base;
    const borePressure = voice.delay[base]! * (1 - fraction) + voice.delay[(base + 1) % size]! * fraction;

    const pressureDifference = clamp(voice.pressure - borePressure * 0.4, -1.5, 1.5);
    let valveFlow: number;
    if (voice.parameters.model === 'reed') {
      const aperture = Math.max(0, 1 - Math.max(0, pressureDifference) * (0.35 + voice.parameters.nonlinearity * 0.6));
      valveFlow = Math.tanh(pressureDifference * (1 + voice.parameters.nonlinearity * 4)) * aperture;
    } else {
      const coefficient = Math.min(0.99, 0.2 + voice.parameters.nonlinearity * 0.75);
      const target = Math.tanh(pressureDifference * (1 + voice.parameters.nonlinearity * 3));
      voice.lipState = flush(coefficient * voice.lipState + (1 - coefficient) * target);
      valveFlow = voice.lipState;
    }
    valveFlow = clamp(Number.isFinite(valveFlow) ? valveFlow * voice.pressure : 0, -1, 1);
    const noise = nextNoise(voice) * Math.abs(valveFlow) * voice.pressure * 0.001;
    const injection = clamp((valveFlow - voice.previousFlow) * 0.001 + noise, -0.4, 0.4);
    voice.previousFlow = valveFlow;

    const filterCoefficient = clamp((1 - voice.parameters.brightness) * TERMINATION_MAX_COEFFICIENT, 0, 0.995);
    const terminated = flush((1 - filterCoefficient) * borePressure + filterCoefficient * voice.terminationState);
    voice.terminationState = terminated;
    const reflection = voice.parameters.model === 'reed' ? -1 : 1;
    const loopFeedback = Math.min(0.9995, 0.9995 - voice.parameters.boreLoss * 0.12);
    const next = clamp(reflection * terminated * loopFeedback + injection, -1, 1);
    voice.delay[voice.writeIndex] = Number.isFinite(next) ? flush(next) : 0;
    voice.writeIndex = (voice.writeIndex + 1) % size;

    if (voice.pressure === 0 && Math.abs(borePressure) < 1e-12 && Math.abs(next) < 1e-12) {
      voice.active = false;
    }
    return borePressure;
  }

  private advancePressure(voice: BoreVoice): void {
    if (voice.releaseSamples > 0) {
      voice.pressure = Math.max(0, voice.pressure - voice.releaseStep);
      voice.releaseSamples -= 1;
      if (voice.releaseSamples === 0) voice.pressure = 0;
      return;
    }
    if (voice.pressure < voice.targetPressure) {
      const step = 1 / (voice.parameters.pressureSlewSeconds * this.sampleRate);
      voice.pressure = Math.min(voice.targetPressure, voice.pressure + step);
    }
  }

  private delayFor(frequencyHz: number, parameters: Readonly<BoreVoiceParameters>): number {
    const tuned = frequencyHz * 2 ** (parameters.detuneCents / 1200);
    bounded('detuned frequencyHz', tuned, 35, this.maximumFrequencyHz);
    const filterCoefficient = clamp((1 - parameters.brightness) * TERMINATION_MAX_COEFFICIENT, 0, 0.995);
    const angularFrequency = 2 * Math.PI * tuned / this.sampleRate;
    const filterPhaseDelay = Math.atan2(
      filterCoefficient * Math.sin(angularFrequency),
      1 - filterCoefficient * Math.cos(angularFrequency),
    ) / angularFrequency;
    const registerDelay = this.sampleRate / tuned / (parameters.model === 'reed' ? 2 : 1);
    const requiredDelayPhase = registerDelay - filterPhaseDelay;
    let low = 1;
    let high = registerDelay;
    for (let iteration = 0; iteration < 32; iteration += 1) {
      const candidate = (low + high) * 0.5;
      const whole = Math.ceil(candidate);
      const interpolationAdvanceWeight = whole - candidate;
      const interpolationAdvance = Math.atan2(
        interpolationAdvanceWeight * Math.sin(angularFrequency),
        1 - interpolationAdvanceWeight + interpolationAdvanceWeight * Math.cos(angularFrequency),
      ) / angularFrequency;
      const phaseDelay = whole - interpolationAdvance;
      if (phaseDelay < requiredDelayPhase) low = candidate;
      else high = candidate;
    }
    return (low + high) * 0.5;
  }

  private validateParameters(parameters: Readonly<BoreVoiceParameters>): void {
    if (parameters.model !== 'reed' && parameters.model !== 'lip') throw new RangeError('model must be reed or lip');
    bounded('detuneCents', parameters.detuneCents, -1200, 1200);
    bounded('boreLoss', parameters.boreLoss, 0, 1);
    bounded('brightness', parameters.brightness, 0, 1);
    bounded('nonlinearity', parameters.nonlinearity, 0, 1);
    bounded('pressureSlewSeconds', parameters.pressureSlewSeconds, 0.001, 0.1);
    bounded('gain', parameters.gain, 0, 1);
  }

  private voice(index: number): BoreVoice {
    return this.voices[integer('voice index', index, 0, BORE_VOICE_COUNT - 1)]!;
  }

  private clearVoiceState(voice: BoreVoice): void {
    voice.delay.fill(0);
    voice.active = false;
    voice.writeIndex = 0;
    voice.terminationState = 0;
    voice.lipState = 0;
    voice.previousFlow = 0;
    voice.pressure = 0;
    voice.targetPressure = 0;
    voice.releaseStep = 0;
    voice.releaseSamples = 0;
    voice.rng = DEFAULT_SEED;
  }
}

class TubeReedLipInstance implements DspModuleInstance {
  private readonly resonator: TubeReedLipResonator;
  private voiceIndex = 0;
  private frequencyHz = 440;
  private pressure = 0.8;
  private seed = 1;

  constructor(sampleRate: number, maxBlockSize: number) {
    this.resonator = new TubeReedLipResonator({ sampleRate, maxBlockSize });
  }

  reset(): void {
    this.resonator.reset();
  }

  setParameter(id: string, value: number): void {
    this.validateParameter(id, value);
    const detuneCents = id === 'detune-cents'
      ? value
      : this.resonator.getVoiceParameters(this.voiceIndex).detuneCents;
    const frequencyHz = id === 'frequency-hz' ? value : this.frequencyHz;
    if (id === 'frequency-hz' || id === 'detune-cents') this.validateTuning(frequencyHz, detuneCents);
    this.applyParameter(id, value);
  }

  process(block: DspProcessBlock): void {
    const left = block.outputs.left;
    const right = block.outputs.right;
    if (!left || !right) throw new RangeError('left/right outputs are required');
    validateCallerBuffers(left, right, block.frames, this.resonator.maxBlockSize);
    const updates = Object.entries(block.controls).filter(([, values]) => values.length > 0).map(([id, values]) => [id, values[0]!] as const);
    for (const [id, value] of updates) this.validateParameter(id, value);
    const updateValue = (id: string): number | undefined => updates.find(([candidate]) => candidate === id)?.[1];
    const voiceIndex = updateValue('voice-index') ?? this.voiceIndex;
    const frequencyHz = updateValue('frequency-hz') ?? this.frequencyHz;
    const detuneCents = updateValue('detune-cents') ?? this.resonator.getVoiceParameters(voiceIndex).detuneCents;
    this.validateTuning(frequencyHz, detuneCents);
    for (const [id, value] of updates) this.applyParameter(id, value);
    this.resonator.processInto(left, right, block.frames);
  }

  private validateParameter(id: string, value: number): void {
    if (id === 'voice-index') integer(id, value, 0, BORE_VOICE_COUNT - 1);
    else if (id === 'frequency-hz') bounded(id, value, 35, this.resonator.maximumFrequencyHz);
    else if (id === 'pressure') bounded(id, value, 0, 1);
    else if (id === 'seed') canonicalSeed(value);
    else if (id === 'gate') {
      if (value !== 0 && value !== 1) throw new RangeError('gate must be 0 or 1');
    } else if (id === 'model') {
      if (value !== 0 && value !== 1) throw new RangeError('model must be 0 (reed) or 1 (lip)');
    } else {
      const ranges: Readonly<Record<string, readonly [number, number]>> = {
        'detune-cents': [-1200, 1200],
        'bore-loss': [0, 1],
        brightness: [0, 1],
        nonlinearity: [0, 1],
        'pressure-slew': [0.001, 0.1],
        gain: [0, 1],
      };
      const range = ranges[id];
      if (!range) throw new RangeError(`unknown parameter: ${id}`);
      bounded(id, value, range[0], range[1]);
    }
  }

  private validateTuning(frequencyHz: number, detuneCents: number): void {
    const tuned = frequencyHz * 2 ** (detuneCents / 1200);
    bounded('detuned frequencyHz', tuned, 35, this.resonator.maximumFrequencyHz);
  }

  private applyParameter(id: string, value: number): void {
    if (id === 'voice-index') this.voiceIndex = value;
    else if (id === 'frequency-hz') this.frequencyHz = value;
    else if (id === 'pressure') this.pressure = value;
    else if (id === 'seed') this.seed = canonicalSeed(value);
    else if (id === 'gate') {
      if (value === 1) this.resonator.noteOn(this.voiceIndex, this.frequencyHz, this.pressure, this.seed);
      else this.resonator.noteOff(this.voiceIndex);
    } else if (id === 'model') this.resonator.setVoiceParameters(this.voiceIndex, { model: value === 0 ? 'reed' : 'lip' });
    else {
      const keys = {
        'detune-cents': 'detuneCents',
        'bore-loss': 'boreLoss',
        brightness: 'brightness',
        nonlinearity: 'nonlinearity',
        'pressure-slew': 'pressureSlewSeconds',
        gain: 'gain',
      } as const;
      const key = keys[id as keyof typeof keys];
      this.resonator.setVoiceParameters(this.voiceIndex, { [key]: value });
    }
  }
}

export const mod011 = defineDspModule({
  descriptor: {
    id: 'mir.mod-011',
    name: 'Tube reed lip resonator',
    version: '0.1.0',
    inputs: [],
    outputs: [
      { id: 'left', kind: 'audio', channels: 1 },
      { id: 'right', kind: 'audio', channels: 1 },
    ],
    parameters: [
      { id: 'voice-index', defaultValue: 0, minValue: 0, maxValue: 7, unit: 'index' },
      { id: 'frequency-hz', defaultValue: 440, minValue: 35, maxValue: 4_000, unit: 'Hz' },
      { id: 'pressure', defaultValue: 0.8, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'seed', defaultValue: 1, minValue: 0, maxValue: 0xffff_ffff, unit: 'uint32' },
      { id: 'gate', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'boolean' },
      { id: 'model', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'enum' },
      { id: 'detune-cents', defaultValue: 0, minValue: -1200, maxValue: 1200, unit: 'cents' },
      { id: 'bore-loss', defaultValue: 0.15, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'brightness', defaultValue: 1, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'nonlinearity', defaultValue: 0.5, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'pressure-slew', defaultValue: 0.01, minValue: 0.001, maxValue: 0.1, unit: 's' },
      { id: 'gain', defaultValue: 0.7, minValue: 0, maxValue: 1, unit: 'ratio' },
    ],
  },
  createInstance: ({ sampleRate, maxBlockSize }) => new TubeReedLipInstance(sampleRate, maxBlockSize),
});

export default mod011;
